import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/hireVehicleReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

export default function HireVehicleReportPrint() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = new URLSearchParams(search);

  const vehicleId = toInt(qs.get("vehicleId"), 0);

  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState(null);

  const handlePrint = () => window.print();

  const fetchOne = async () => {
    if (!vehicleId) return;

    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/print/one`, {
        params: { vehicleId },
      });

      if (!res.data?.success) throw new Error(res.data?.message || "Failed");
      setDetails(res.data.row || null);
    } catch (e) {
      alert(e.message || "Failed to load print data");
      setDetails(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOne();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId]);

  // helpers to safely read fields (different DB column names)
  const get = (obj, ...keys) => {
    for (const k of keys) {
      if (obj && obj[k] !== undefined && obj[k] !== null && String(obj[k]).trim() !== "") return obj[k];
    }
    return "";
  };

  const vehicleDetails = {
    branch: get(details, "fld_branchname", "branch"),
    store: get(details, "fld_storename", "store"),
    vehicleSubType: get(details, "fld_vehicle_subtypename", "vehicleSubType"),
    hiredFrom: get(details, "fld_hiredfrom", "hiredFrom"),
    vehicleName: get(details, "fld_vehicle_name", "vehicleName"),
    vehicleRegNo: get(details, "fld_vehicle_regno", "vehicleRegNo"),
    vehicleCode: get(details, "fld_vehicle_code", "vehicleCode"),
    address: get(details, "fld_address", "address"),
    vehicleModel: get(details, "fld_vehiclemodel", "vehicleModel"),
    city: get(details, "fld_city", "city"),
    vehicleBasis: get(details, "fld_vehiclebasis", "vehicleBasis"),
    telephoneNo: get(details, "fld_telephone", "telephoneNo"),
    vehicleRent: get(details, "fld_vehiclerent", "vehicleRent"),
    ownerName: get(details, "fld_ownername", "ownerName"),
    chqFavName: get(details, "fld_chqfavname", "chqFavName"),
    contactPerson: get(details, "fld_contactperson", "contactPerson"),
    tdsPercentage: get(details, "fld_tdspercent", "tdsPercentage"),
    mobileNo: get(details, "fld_mobileno", "mobileNo"),
    fuelType: get(details, "fld_fueltype", "fuelType"),
    mailingAddress: get(details, "fld_mailingaddress", "mailingAddress"),
    icExpire: get(details, "fld_icexpire", "icExpire"),
    validFrom: get(details, "fld_validfrom", "validFrom"),
    fcExpire: get(details, "fld_fcexpire", "fcExpire"),
    validUpto: get(details, "fld_validupto", "validUpto"),
    adminApproved: get(details, "fld_adminapproved", "adminApproved"),
    pancardNo: get(details, "fld_pancardno", "pancardNo"),
    permitExpire: get(details, "fld_permitexpire", "permitExpire"),
    pancardHolder: get(details, "fld_pancardholder", "pancardHolder"),
    remarks: get(details, "fld_remarks", "remarks"),
    foodForDriver: get(details, "fld_foodfordriver", "foodForDriver"),
  };

  return (
    <div className="min-h-screen bg-white p-4">
      {/* Buttons hidden during print */}
      <div className="print:hidden mb-6 text-center flex gap-3 justify-center">
        <button
          onClick={() => navigate(-1)}
          className="bg-gray-200 text-black px-6 py-3 text-sm font-medium hover:bg-gray-300"
        >
          Back
        </button>

        <button
          onClick={handlePrint}
          disabled={loading || !details}
          className="bg-black text-white px-6 py-3 text-sm font-medium hover:bg-gray-800 transition-colors disabled:opacity-60"
        >
          {loading ? "Loading..." : "Print Report"}
        </button>
      </div>

      <div className="max-w-4xl mx-auto border-2 border-black p-4 print:max-w-none print:mx-0 print:border-2">
        <div className="border border-black p-3">
          {/* Header */}
          <div className="border border-black p-3 mb-4 flex items-center gap-5">
            <div className="w-28 h-20 bg-black text-white flex flex-col justify-center items-center text-xs font-bold">
              <div>NISSI</div>
              <div>INFOTECH</div>
              <div className="text-xs">PVT LTD</div>
            </div>

            <div className="flex-1 text-center">
              <h1 className="text-lg font-bold mb-2">Nissi Infotech</h1>
              <div className="text-xs leading-tight">
                <div>Nissi</div>
                <div className="font-semibold">CIVIL CONSTRUCTION</div>
                <div>3/42-12, Nagappa colony , kavundampalayam ,Tamil Nadu, India .</div>
                <div className="mt-1">
                  Phone No :9003744644 &nbsp;&nbsp;&nbsp;&nbsp; infraconsoft-implementation@nissiinfotech.in
                </div>
              </div>
            </div>
          </div>

          <div className="text-center text-base font-bold my-4">Hire Vehicle Report</div>

          {!details && !loading ? (
            <div className="text-center text-sm p-6 border border-black">No data found for VehicleId: {vehicleId}</div>
          ) : (
            <div className="grid grid-cols-2 gap-8 text-xs">
              {/* Left */}
              <div className="space-y-3">
                {[
                  ["Branch", vehicleDetails.branch],
                  ["Vehicle Sub Type", vehicleDetails.vehicleSubType],
                  ["Vehicle Name", vehicleDetails.vehicleName],
                  ["Vehicle Code", vehicleDetails.vehicleCode],
                  ["Vehicle Model", vehicleDetails.vehicleModel],
                  ["Vehicle basis", vehicleDetails.vehicleBasis],
                  ["Vehicle Rent", vehicleDetails.vehicleRent],
                  ["Chq fav name", vehicleDetails.chqFavName],
                  ["TDS Percentage", vehicleDetails.tdsPercentage],
                  ["Fuel Type", vehicleDetails.fuelType],
                  ["IC Expire", vehicleDetails.icExpire],
                  ["FC Expire", vehicleDetails.fcExpire],
                  ["Admin Approved", vehicleDetails.adminApproved],
                  ["Permit Expire", vehicleDetails.permitExpire],
                  ["Remarks", vehicleDetails.remarks],
                ].map(([k, v]) => (
                  <div className="flex" key={k}>
                    <span className="font-semibold w-32">{k}</span>
                    <span className="mx-2">:</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>

              {/* Right */}
              <div className="space-y-3">
                {[
                  ["Store", vehicleDetails.store],
                  ["Hired From", vehicleDetails.hiredFrom],
                  ["Vehicle Reg No.", vehicleDetails.vehicleRegNo],
                  ["Address", vehicleDetails.address],
                  ["City", vehicleDetails.city],
                  ["Telephone No.", vehicleDetails.telephoneNo],
                  ["Owner's Name", vehicleDetails.ownerName],
                  ["Contact Person", vehicleDetails.contactPerson],
                  ["Mobile No.", vehicleDetails.mobileNo],
                  ["Mailing Address", vehicleDetails.mailingAddress],
                  ["Valid From", vehicleDetails.validFrom],
                  ["Valid Upto", vehicleDetails.validUpto],
                  ["Pancard No", vehicleDetails.pancardNo],
                  ["Pancard Holder", vehicleDetails.pancardHolder],
                  ["Food For Driver", vehicleDetails.foodForDriver],
                ].map(([k, v]) => (
                  <div className="flex" key={k}>
                    <span className="font-semibold w-32">{k}</span>
                    <span className="mx-2">:</span>
                    <span>{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 p-2 border border-black bg-gray-100 text-xs">
            Report generated by ERP-Software - www.infraconsoft.com owned by www.nissiinfotech.com
          </div>
        </div>
      </div>

      <style jsx>{`
        @media print {
          body { margin: 0; padding: 0; }
          * { -webkit-print-color-adjust: exact !important; color-adjust: exact !important; print-color-adjust: exact !important; }
          .print\\:hidden { display: none !important; }
          .print\\:max-w-none { max-width: none !important; }
          .print\\:mx-0 { margin-left: 0 !important; margin-right: 0 !important; }
          .print\\:border-2 { border-width: 2px !important; }
          .grid { page-break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
